import { useLazyQuery } from "@apollo/client";
import cn from "classnames";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";

import { GET_ALL_PAGES_USER } from "@api/queriesV2/cms";
import BlockTitle from "@components/cms/BlockTitle/BlockTitle";
import { getPageFolderName, getPageTypes } from "@config/cms";
import { blurDataURL } from "@config/config";
import { useIsMounted } from "@hooks/useIsMounted";
import { useStore } from "@hooks/useZustandProvider";
import i from "@styles/icons.module.scss";
import Container from "@ui-kit/Container/Container";
import Loading from "@ui-kit/Loading/Loading";
import { localizedDate } from "@utils/utils";

import s from "./LatestListBlock.module.scss";

const LatestListBlock = ({ data, settings, updateField }) => {
  const isMounted = useIsMounted();
  const { t } = useTranslation();
  const { blockType } = settings.common;
  const {
    contentType,
    itemsAmount,
    showPicture,
    showDescription,
    showDate,
    showLoadMore,
  } = settings.specific;
  const { title } = data;
  const { cms, getBlockLocale } = useStore();
  const { isEditMode } = cms.uiOptions;
  const { locale } = useRouter();
  const [list, setList] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [showMore, setShowMore] = useState(true);
  const PAGE_SIZE = +itemsAmount.value;
  const lang = getBlockLocale(locale);

  const onLoadMore = () => {
    setPageNumber(pageNumber + 1);
  };

  const [getList, { loading }] = useLazyQuery(GET_ALL_PAGES_USER, {
    context: { clientName: "v2" },
    variables: {
      language: lang,
      type: getPageTypes()[contentType.value],
      pageNumber,
      pageSize: PAGE_SIZE,
    },
    onCompleted: (data) => {
      setList((state) => [...state, ...data.getAllPagesUser]);
    },
    onError: (error) => {
      toast.error(`[${error.name}]: ${error.message}`);
    },
  });

  useEffect(() => {
    const fetchData = () => {
      getList({
        variables: {
          language: lang,
          type: getPageTypes()[contentType.value],
          pageNumber,
          pageSize: PAGE_SIZE,
        },
      });
    };

    if (isMounted) {
      fetchData();
    }
  }, [pageNumber, isEditMode, lang, contentType.value, PAGE_SIZE, isMounted]);

  useEffect(() => {
    if (isMounted) {
      setList([]);
      setPageNumber(1);
    }
  }, [lang, contentType.value, itemsAmount.value, isMounted]);

  useEffect(() => {
    setShowMore(pageNumber * PAGE_SIZE === list.length);
  }, [list.length]);

  return (
    <Container className={s.latest}>
      <Container full={false}>
        <section>
          <BlockTitle
            settings={settings}
            field={{ title }}
            onChange={updateField}
            type={blockType}
          />
          {pageNumber === 1 && loading ? (
            <Loading />
          ) : list?.length ? (
            <>
              <div className={s.list}>
                {list.map((page, key) => {
                  const { isPublished } = page;
                  const { slug } = page.data;
                  const { title, description, thumbnail } = page.data.seo;
                  const { datePublished } = page.properties;

                  return (
                    <Link
                      key={key}
                      href={`/${getPageFolderName()[contentType.value]}/${slug}`}
                      className={s.item}
                    >
                      {showPicture.value &&
                        (thumbnail ? (
                          <div className={s.image}>
                            <Image
                              fill
                              className={s.img}
                              // quality={100}
                              src={thumbnail}
                              blurDataURL={blurDataURL}
                              placeholder="blur"
                              alt={title}
                              // sizes="(max-width: 630px) 100vw, 400px"
                            />
                          </div>
                        ) : (
                          <div className={s.storyBanner}>
                            <div>banner</div>
                          </div>
                        ))}
                      {showDate.value && (
                        <div className={s.date}>
                          {datePublished
                            ? localizedDate(
                                datePublished,
                                "dd MMMM yyyy",
                                locale,
                              )
                            : "- - -"}
                          {!isPublished && (
                            <div className={!isPublished && s.unpublishedText}>
                              {t("unpublished")}
                            </div>
                          )}
                        </div>
                      )}
                      <h5
                        className={cn(s.title, !isPublished && s.unpublished)}
                      >
                        {title}
                      </h5>
                      {showDescription.value && (
                        <div
                          className={cn(
                            s.description,
                            !isPublished && s.unpublished,
                          )}
                        >
                          {description}
                        </div>
                      )}
                    </Link>
                  );
                })}
              </div>
              {showLoadMore.value && showMore && (
                <div className={s.showMoreWrap}>
                  {loading ? (
                    <Loading />
                  ) : (
                    <div className={s.showMore} onClick={onLoadMore}>
                      {t("show more")}
                      <icon className={i.arrowRight_v2} />
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className={s.newsList}>No news yet.</div>
          )}
        </section>
      </Container>
    </Container>
  );
};

export default LatestListBlock;
